import { useEffect, useRef, useState } from 'react';
import { createMerchantSecurityClient, securityErrorMessage } from './merchantSecurity';
import { validatePinPolicy } from './pinPolicy';
import SecurityChallenge from './SecurityChallenge';

export default function PinWithdrawalApproval({ confirmation, onReady, summary }) {
    const [client] = useState(() => createMerchantSecurityClient());
    const [settings, setSettings] = useState(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [verified, setVerified] = useState(false);
    const [generation, setGeneration] = useState(0);
    const alive = useRef(true);
    const locked = useRef(false);
    const callback = useRef(onReady); callback.current = onReady;
    const load = async () => {
        if (locked.current) return;
        locked.current = true; setBusy(true); setMessage(''); setSettings(null); setVerified(false); setGeneration(n => n + 1); callback.current(null);
        try {
            const data = validatePinPolicy(await client.settings());
            if (!alive.current) return;
            setSettings(data);
            if (!data.enabled || data.withdraw.required === false) callback.current({ requestId: confirmation.requestId, required: false });
        } catch (e) { if (alive.current) setMessage(securityErrorMessage(e)); }
        finally { locked.current = false; if (alive.current) setBusy(false); }
    };
    const loader = useRef(load); loader.current = load;
    useEffect(() => {
        alive.current = true; void loader.current();
        return () => { alive.current = false; client.clear(); };
    }, [client]);
    const blocked = settings?.recovery_required || Number(settings?.locked_until) > Date.now() || !settings?.pin_registered;
    const needsPin = settings?.enabled && settings.withdraw.required;
    const showChallenge = needsPin && !blocked && !verified;
    return <section className="pin-ui transfer-pin-approval" aria-busy={busy}>
        {busy && <p role="status">출금 인증 정책 확인 중입니다.</p>}
        {message && <p className="pin-error" role="alert">{message}</p>}
        {needsPin && <>
            <p className="transfer-pin-step">{verified ? '계좌 확인 · PIN 인증 완료' : '계좌 확인 완료 · PIN 인증'}</p>
            <h3>{verified ? 'PIN 인증이 완료되었습니다' : '송금 PIN을 입력해주세요'}</h3>
            <p className="transfer-pin-help">{verified ? '받는 계좌와 금액을 확인하고 송금을 승인해주세요.' : '등록한 4자리 PIN으로 본인 확인을 진행합니다.'}</p>
            {blocked && <p className="pin-error" role="alert">PIN 등록·잠금·복구 상태를 확인해주세요. PIN 관리에서 진행할 수 있습니다.</p>}
        </>}
        {showChallenge ? <SecurityChallenge key={generation} client={client} purpose="withdraw" method="PIN" polished autoStart continuous submitLabel="PIN 확인" beforeSubmit={summary}
            request={{ amount: confirmation.amount, bank_cd: confirmation.recipient.bankCd, accnt_no: confirmation.recipient.account, request_id: confirmation.requestId }}
            onVerified={proof => { setVerified(true); callback.current({ ...proof, required: true, requestId: confirmation.requestId, csrfToken: settings.csrf_token }); }}
            onSessionError={e => { setSettings(null); setMessage(securityErrorMessage(e)); callback.current(null); client.clear(); }} /> : summary}
        {showChallenge && <p className="transfer-pin-help transfer-pin-footer-note">인증 완료 후 송금을 최종 확인해주세요.</p>}
        {(message || (needsPin && blocked)) && <button type="button" className="pin-back" disabled={busy} onClick={load}>인증 설정 다시 조회</button>}
    </section>;
}
